import { Component, Input, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReportBugDialogComponent } from '../report-bug-dialog/report-bug-dialog.component';

/**
 * The always-present way to report a problem. Lives at the bottom of the side nav.
 *
 * It was a floating action button first. Moved into the nav because a fixed-position circle over
 * the bottom-right corner covers map controls and table pagination on exactly the pages people
 * most want to report, and because the nav is where users already look for app-level actions.
 *
 * Mounted by `SideNavComponent`, which `NavBarComponent` renders once at the app root and never
 * recreates on navigation, so it is on every authenticated page including one that failed to load.
 * That is the point: the page a user most wants to report is the one that did not work.
 *
 * No role check and no feature flag. The Back Office accepts a report from any authenticated user
 * precisely so somebody who can reach nothing can still say so, and a role gate here would undo
 * that on this side.
 */
@Component({
  selector: 'app-report-bug-button',
  templateUrl: './report-bug-button.component.html',
  standalone: false,
})
export class ReportBugButtonComponent {
  private readonly dialog = inject(MatDialog);

  /** Mirrors every other side nav item: icon only when collapsed, icon and label when open. */
  @Input() sideBarOpened = false;

  open(): void {
    this.dialog.open(ReportBugDialogComponent, {
      maxWidth: '95vw',
      // The dialog caps itself at 85vh and scrolls its middle, but the overlay panel needs a cap
      // too or it grows past the viewport and takes the actions with it.
      maxHeight: '90vh',
      // Strips the surface's own padding, which sits outside the component's height cap and was
      // what pushed the actions off-screen. The component supplies its own padding.
      panelClass: 'bug-report-dialog-panel',
      autoFocus: false,
      // The dialog holds a capture the user may already have redacted, so a mis-click on the
      // backdrop must not throw that away.
      disableClose: true,
    });
  }
}
